import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { BROUTER_MAP_CONFIG } from '../../config/brouter-map.config';
import { BRouterSegment, ExportFormat, LatLng } from '../../models/brouter-map.model';
import {
  concatTotalTrack,
  formatTrack,
  MIME_TYPES,
  sanitiseTrackname
} from '../../services/track-export';

const FORMATS: { value: ExportFormat; label: string }[] = [
  { value: 'gpx', label: 'GPX' },
  { value: 'kml', label: 'KML' },
  { value: 'geojson', label: 'GeoJSON' },
  { value: 'csv', label: 'CSV' }
];

/**
 * Export dialog, replacing the legacy `#export` modal.
 *
 * Files are built in the browser from the routed legs rather than re-requested from the
 * engine, which is what the legacy client preferred — the engine does not know about
 * straight-line legs and would silently drop them.
 */
@Component({
  selector: 'app-export-dialog',
  imports: [FormsModule, TranslateModule],
  templateUrl: './export-dialog.component.html',
  styleUrl: './export-dialog.component.scss'
})
export class ExportDialogComponent {
  readonly segments = input.required<readonly (BRouterSegment | null)[]>();
  readonly waypoints = input.required<readonly LatLng[]>();
  readonly hasBeeline = input(false);

  readonly closed = output<void>();

  private readonly config = inject(BROUTER_MAP_CONFIG);

  readonly formats = FORMATS;
  readonly selectedFormat = signal<ExportFormat>('gpx');
  readonly trackname = signal('brouter');
  readonly includeWaypoints = signal(false);

  /** The server strips anything outside this set, so the same is shown here. */
  readonly sanitisedTrackname = computed(() =>
    sanitiseTrackname(this.trackname(), this.config.tracknameAllowedChars)
  );
  readonly tracknameWasChanged = computed(() => this.sanitisedTrackname() !== this.trackname());

  readonly canExport = computed(() => this.segments().some(segment => segment !== null));

  download(): void {
    const track = concatTotalTrack(this.segments());
    const format = this.selectedFormat();
    const name = this.sanitisedTrackname() || 'brouter';

    const content = formatTrack(format, track, {
      trackname: name,
      waypoints: this.includeWaypoints() ? this.waypoints() : undefined
    });

    const blob = new Blob([content], { type: MIME_TYPES[format] });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}.${format}`;
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    this.closed.emit();
  }
}
